import type { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxAddress, OnyxCoordinates, OnyxOptional } from '@onyx/angular';
import {
  AddressHelper,
  OnyxMapVehicleStatus,
  OnyxScheduleCompliance,
} from '@onyx/angular';
import { ceil, chain, isEqual, omit } from 'lodash';
import { DateTime } from 'luxon';
import { ScheduleComplianceHelper } from '../../helpers/schedule-compliance.helper';
import { FleetCategory } from '../../../dashboard/fleet/common/enums/fleet-category';
import { FleetTypeHelper } from '../../../dashboard/fleet/common/helpers/fleet-type.helper';
import { TelematicsHelper } from '../../../dashboard/map/common/helpers/telematics.helper';
import { OrderPointCategory } from '../../../dashboard/orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../../dashboard/orders/common/enums/order-point-status';
import { OrderStatus } from '../../../dashboard/orders/common/enums/order-status';
import { OrderHelper } from '../../../dashboard/orders/common/helpers/order.helper';
import type { OrderLoadingPoint } from '../../../dashboard/orders/common/interfaces/order-point';
import type { OrderPointAction } from '../../../dashboard/orders/common/interfaces/order-point-action';
import { DeadlineHelper } from '../../helpers/deadline.helper';
import { RouteHelper } from '../../helpers/route.helper';
import { RoutePointValueHelper } from '../../helpers/route-point-value.helper';
import type { Point } from '../../interfaces/common/point';
import type { OffRouteData } from '../../interfaces/route/off-route-data';
import type { TelematicsDelegation } from '../../interfaces/telematics/telematics-delegation';
import type { TelematicsFleetData } from '../../interfaces/telematics/telematics-event';
import type {
  TelematicsFleet,
  TelematicsFleetCargo,
  TelematicsFleetStatus,
} from '../../interfaces/telematics/telematics-fleet';
import type { TelematicsItem } from '../../interfaces/telematics/telematics-item';
import type { TelematicsRoutePoint } from '../../interfaces/telematics/telematics-route-point';
import type { TelematicsTelemetry } from '../../interfaces/telematics/telematics-telemetry';
import { TelematicsStore } from '../../stores/telematics.store';

export class TelematicsFleetFactory {
  public static from(data: {
    fleet: TelematicsFleetData;
    telemetry: TelematicsTelemetry | null;
    activeDelegations: TelematicsDelegation[];
    routePoints: TelematicsRoutePoint[];
    now: DateTime;
    previous: TelematicsItem | null;
    injector: Injector;
  }): TelematicsFleet {
    const telematicsStore = data.injector.get(TelematicsStore);
    const translateService = data.injector.get(TranslateService);

    const points = TelematicsHelper.getPoints(data.routePoints);
    const currentPointData = data.routePoints.find((point) => {
      if (point.order?.status.value === OrderStatus.TO_ACCEPT) return false;

      const isFinished = [
        OrderPointStatus.COMPLETED,
        OrderPointStatus.CANCELED,
      ].includes(RouteHelper.getStatus(point)!);

      return RouteHelper.isDelegationPoint(point) && !isFinished
        ? RouteHelper.isReturningDelegationPoint(point)
        : !isFinished;
    });
    const deadlineTime = RouteHelper.getPointDeadlineTime(
      currentPointData?.point,
    );
    const realizationTime = RouteHelper.getPointRealizationTime(
      currentPointData?.point,
    );

    const currentPoint: TelematicsRoutePoint | null = currentPointData
      ? {
          ...currentPointData,
          deadlineTime,
          bufferTime:
            deadlineTime && realizationTime
              ? realizationTime.diff(deadlineTime)
              : null,
        }
      : null;

    const lastRoutePoint =
      data.routePoints.findLast(
        (routePoint) =>
          !RouteHelper.isDelegationPoint(routePoint) &&
          RouteHelper.isCompleted(routePoint),
      ) ?? null;
    const lastPoint = lastRoutePoint
      ? RouteHelper.getPointValue(lastRoutePoint)
      : null;

    const status = ((): TelematicsFleetStatus => {
      const freeSinceAt =
        FleetTypeHelper.isVehicle(data.fleet) && lastPoint
          ? (lastPoint?.timestamps.completedAt ??
            RoutePointValueHelper.getEstimations(lastPoint)?.departureTime ??
            points
              .toReversed()
              .map((point) => DeadlineHelper.getPointDeadline(point)?.departure)
              .find(Boolean)
              ?.toISO())
          : null;
      const freeSince = freeSinceAt
        ? DateTime.fromISO(freeSinceAt)
        : telematicsStore.DATA_START();
      const freeSinceLocation = lastPoint
        ? this.getPointAddress(lastPoint)
        : data.telemetry
          ? AddressHelper.getCountryCode(data.telemetry.coordinates)
          : null;
      const isFreeOver24h = data.now.diff(freeSince, 'hours').hours > 24;

      const category = data.fleet.generalInformation.category;

      const getIcon = (
        status: OnyxMapVehicleStatus,
        action?: OnyxOptional<OrderPointAction>,
      ) => ({
        name:
          action?.icon ?? TelematicsHelper.getTelematicsFleetStatusIcon(status),
        tooltip:
          action?.tooltip ??
          TelematicsHelper.getTelematicsFleetStatusTooltip(
            status,
            translateService,
          ),
        color:
          TelematicsHelper.getTelematicsFleetStatusIconColor(status) ?? 'black',
      });

      switch (category) {
        case FleetCategory.SEMI_TRUCK:
        case FleetCategory.STRAIGHT_TRUCK:
        case FleetCategory.VAN: {
          if (currentPoint) {
            const futurePoints = points.slice(
              data.routePoints.findIndex((point) =>
                RouteHelper.isEqual(data.routePoints, point, currentPoint),
              ) + 1,
            );
            const futureDelayedPoints = futurePoints
              .map((point) =>
                RouteHelper.getScheduleCompliance(point, futurePoints),
              )
              .filter(
                (compliance) => compliance === OnyxScheduleCompliance.DELAYED,
              ).length;

            const compliance =
              RouteHelper.getScheduleCompliance(currentPoint, futurePoints) ??
              OnyxScheduleCompliance.ON_TIME;
            const complianceColor =
              ScheduleComplianceHelper.getColor(compliance);
            const value = {
              [OnyxScheduleCompliance.ON_TIME]:
                OnyxMapVehicleStatus.RIDE_ON_TIME,
              [OnyxScheduleCompliance.DELAY_RISK]:
                OnyxMapVehicleStatus.RIDE_DELAY_RISK,
              [OnyxScheduleCompliance.DELAYED]:
                OnyxMapVehicleStatus.RIDE_DELAYED,
            }[compliance];

            const action =
              RouteHelper.getStatus(currentPoint) !== OrderPointStatus.TO_DO
                ? RouteHelper.getPointAction(
                    RouteHelper.getPointValue(currentPoint),
                    RouteHelper.getStatus(currentPoint),
                    compliance,
                    true,
                    translateService,
                  )
                : null;

            const offRouteData = data.telemetry
              ? this.getOffRoute(
                  currentPoint,
                  data.telemetry.coordinates,
                  data.previous,
                )
              : null;

            return {
              value,
              icon: getIcon(value, action),
              freeSince,
              freeSinceLocation,
              action,
              compliance,
              complianceColor,
              futureDelayedPoints,
              offRouteData,
            };
          }

          if (data.activeDelegations.length) {
            const value = isFreeOver24h
              ? OnyxMapVehicleStatus.FREE_DELEGATION_OVER_24H
              : OnyxMapVehicleStatus.FREE_DELEGATION_UNDER_24H;

            return {
              value,
              icon: getIcon(value),
              freeSince,
              freeSinceLocation,
            };
          }

          const value = isFreeOver24h
            ? OnyxMapVehicleStatus.FREE_OVER_24H
            : OnyxMapVehicleStatus.FREE_UNDER_24H;

          return {
            value,
            icon: getIcon(value),
            freeSince,
            freeSinceLocation,
          };
        }

        case FleetCategory.SEMI_TRAILER:
        case FleetCategory.TRAILER: {
          const isSemiTrailer = category === FleetCategory.SEMI_TRAILER;

          const value = (() => {
            if (!currentPoint) {
              return isSemiTrailer
                ? OnyxMapVehicleStatus.SEMI_TRAILER_FREE
                : OnyxMapVehicleStatus.TRAILER_FREE;
            }

            if (!OrderHelper.hasApprovedVehicle(currentPoint.order)) {
              const nextPickupPoint =
                TelematicsHelper.getNextLoadedSemiTrailerPickupPoint(
                  data.routePoints,
                  data.fleet.uuid,
                );

              if (
                !nextPickupPoint ||
                currentPoint.order?.uuid === nextPickupPoint.order?.uuid
              ) {
                return isSemiTrailer
                  ? OnyxMapVehicleStatus.SEMI_TRAILER_LOADED
                  : OnyxMapVehicleStatus.TRAILER_LOADED;
              }
            }

            return isSemiTrailer
              ? OnyxMapVehicleStatus.SEMI_TRAILER_LOADED_WAITING
              : OnyxMapVehicleStatus.TRAILER_LOADED_WAITING;
          })();

          return {
            value,
            icon: getIcon(value),
            freeSince,
            freeSinceLocation,
          };
        }
      }
    })();

    const cargo = ((): TelematicsFleetCargo | null => {
      const fleet = data.fleet.assignedTrailer ?? data.fleet;
      const weightCapacity = fleet.cargoSpace.loadCapacity;
      if (!fleet.generalInformation.type || !weightCapacity) return null;

      const points = TelematicsHelper.getPoints(data.routePoints);
      const loadedGoods = new Map(
        points
          .filter(
            (point): point is OrderLoadingPoint =>
              point.category === OrderPointCategory.LOADING,
          )
          .filter(({ status }) => status === OrderPointStatus.COMPLETED)
          .flatMap(({ goods }) => goods)
          .map((good) => [good.uuid, good] as const),
      );
      const weight = chain(points)
        .filter((point) => point.category === OrderPointCategory.UNLOADING)
        .filter(({ status }) => status !== OrderPointStatus.COMPLETED)
        .flatMap(({ goods }) => goods)
        .map((good) => ({
          good: loadedGoods.get(good.uuid),
          quantity: good.quantity,
        }))
        .filter(({ good }) => good != null)
        .sumBy(
          ({ good, quantity }) =>
            (quantity / (OrderHelper.getGoodQuantity(good) ?? 1)) *
            good!.totalWeight,
        )
        .value();

      return {
        category: fleet.generalInformation.category,
        type: fleet.generalInformation.type,
        weight: ceil(weight, 1),
        weightCapacity,
      };
    })();

    return {
      ...omit(data.fleet, ['status', 'drivers', 'delegations']),
      documentsStatus: data.fleet.status,
      status,
      cargo,
      currentPoint,
      lastPoint: lastRoutePoint,
    };
  }

  private static getOffRoute(
    currentPoint: TelematicsRoutePoint,
    coordinates: OnyxCoordinates,
    previous: TelematicsItem | null,
  ): OffRouteData | null {
    if (
      (previous?.currentPoint?.point ?? previous?.currentPoint?.technicalPoint)
        ?.uuid === RouteHelper.getUuid(currentPoint) &&
      isEqual(previous?.currentPoint?.route, currentPoint.route) &&
      isEqual(previous?.telemetry?.coordinates, coordinates) &&
      previous?.status.offRouteData
    ) {
      return previous.status.offRouteData;
    }

    return RouteHelper.getOffRouteData(
      currentPoint.route,
      coordinates,
      this.getPointAddress(RouteHelper.getPointValue(currentPoint)).coordinates,
    );
  }

  private static getPointAddress(point: Point): OnyxAddress {
    if ('address' in point) return point.address;
    return point.endAddress;
  }
}
